import { useState } from 'react';
import { useGetMachinesQuery } from '../services/Machines/MachinesService';
import useDebounce from './useDebounce';
import { ASSET_STATUS } from '../constants/assetStatus';

/**
 * Custom hook for asset search functionality
 * Provides validation, debouncing, and API query logic
 *
 * @param {Object} options - Configuration options
 * @param {number} options.debounceMs - Debounce delay in milliseconds (default: 600)
 * @param {number} options.perPage - Number of results per page (default: 10)
 * @param {number} options.page - Page number (default: 1)
 * @returns {Object} Asset search state and handlers
 */
export const useAssetSearch = (options = {}) => {
  const { debounceMs = 600, perPage = 10, page = 1 } = options;
  const [inputValue, setInputValue] = useState('');
  const debouncedInputValue = useDebounce(inputValue, debounceMs);

  const isValidInput = debouncedInputValue === '' || /^\d+$/.test(debouncedInputValue);

  const statusIds = [ASSET_STATUS.WAREHOUSE, ASSET_STATUS.ACTIVE, ASSET_STATUS.IN_TRANSIT, ASSET_STATUS.ON_ORDER].join(
    ',',
  );
  const searchParams = isValidInput ? `id=${debouncedInputValue}&statusId=${statusIds}` : `statusId=${statusIds}`;

  const {
    currentData: assetData,
    isLoading: isAssetLoading,
    isError: isAssetError,
  } = useGetMachinesQuery(
    {
      searchParams,
      perPage,
      page,
    },
    {
      skip: !isValidInput,
    },
  );

  const assetOptions =
    assetData?.assets?.map((item) => ({
      text: item.title ? `${item.id} (${item.title})` : String(item.id),
      value: item.id,
    })) || [];

  const handleInputChange = (value) => {
    const numericValue = value.split(' ')[0];
    if (numericValue === '' || /^\d+$/.test(numericValue)) {
      setInputValue(numericValue);
    }
  };

  const clearInput = () => {
    setInputValue('');
  };

  const getHelperText = (selectedValue) => {
    if (isAssetError) return 'Unable to search assets. Please enter a valid Asset ID number.';

    const hasValue = selectedValue?.value || selectedValue;
    if (hasValue) return '';
    if (assetOptions.length === 0 && debouncedInputValue && !isAssetLoading) return 'No assets found';

    return '';
  };

  return {
    inputValue,
    debouncedInputValue,
    isValidInput,
    assetOptions,
    isAssetError,
    isAssetLoading,
    handleInputChange,
    clearInput,
    setInputValue,
    getHelperText,
  };
};
